import React, { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Grid } from "@material-ui/core";
import { Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress } from "@mui/material";
import { uploadFile } from "../../../../shared/services/FileUploadService";
import { saveAttachments } from "../../../../../redux/actions/lenderRequestActions";
import { REQUEST_TYPES } from "../../../../shared/constants/RequestTypes";
import { isNumeric } from "../../../../shared/utilities/number-utils";

const ReviewAndSubmitPage = ({
  projectInfo,
  formState,
  incrementCounter,
  decrementCounter,
  projectId
}) => {
  const { handleSubmit } = useForm();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const dispatch = useDispatch();
  const requestTypeId = useSelector(state => state.lenderRequestReducer.requestTypeId);

  // Extract form state data
  const {
    requestType,
    addUnavailable,
    selectedReasonCodes = [],
    comments,
    uploadedFiles = {},
    requiredDocuments = [],
    selectedFiles = [],
    existingReasonCodes = [],
    addedReasonCodesList = [],
    removedReasonCodes = []
  } = formState;

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      const lenderId =
        sessionStorage.getItem("lenderId") ||
        passData.sellerServicerCoNo ||
        "12346";

      const requestPayload = {
        requestTypeId: requestTypeId, 
        projectId: projectId,
        lenderId: lenderId,
        createdBy: passData.user,
        notes: comments,
        reasonCodes: (requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
                     requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS)
          ? selectedReasonCodes.map(rc => rc.value)
          : existingReasonCodes.map(rc => rc.reasonId)
      };

      // Step 1: Submit the request to get the requestId
      const response = await axios.post(
        `${global.REACT_APP_API_ENDPOINT}/lender/request/status-change`,
        requestPayload
      );

      const requestId = response.data;

      // If requestId is not returned or not a valid number, display an error and throw an exception to skip the
      // rest of the submission process
      if (!requestId || !isNumeric(requestId)) {
        const errorMessage = "Failed to submit request. Invalid request ID returned.";
        throw new Error(errorMessage);
      }

      // Step 2: If there are files to upload, upload them now
      if (selectedFiles && selectedFiles.length > 0) {
        
        const uploadResults = [];
        
        const uploadTasks = selectedFiles.map((fileObj, i) => {
          let resolve, reject;

          const promise = new Promise((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
          });

          const onSuccess = (data) => {
            const result = {
              documentRankingId: fileObj.docType.documentRankingId,
              docMapId: data?.docMapId,
              note: fileObj.note
            };
            uploadResults.push(result);
            resolve(result);
          };   

          const onFail = (error) => {
            console.error('upload error callback ' + i, {i, error, fileObj});
            reject(error);
          };

          uploadFile(
            fileObj.file, 
            fileObj.note, 
            null, 
            null, // phaseId
            projectId,
            fileObj.file.name,
            onSuccess, 
            onFail
          )
          .catch(onFail);      
          
          return promise;
        });

        const results = await Promise.allSettled(uploadTasks);
        const succeeded = results.filter(result => result.status === "fulfilled");
        const failed = results.filter(result => result.status === "rejected");

        if (succeeded.length === results.length) {
          // Step 3: Save the attachments to the request
          const saveResult = await dispatch(saveAttachments(requestId, uploadResults));
          if (!saveResult) {
            throw new Error("Failed to save attachments to request");
          }
        } else {
          throw new Error(`${failed.length} file upload(s) failed`);
        }
      }

      // Success - proceed to next step
      async function requestCallback() {
        return { requestId };
      }

      incrementCounter(formState, requestCallback);
      
    } catch (error) {
      console.error("Error submitting request:", error);
      setSubmitError(error.response?.data?.message || error.message || "An error occurred while submitting your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    decrementCounter(formState);
  };

  const formatReasonCodes = () => {
    if (!selectedReasonCodes.length) return "None selected";
    return selectedReasonCodes.map(rc => rc.label).join(", ");
  };

  const formatUploadedFiles = () => {
    const fileCount = selectedFiles?.length || 0;
    if (fileCount === 0) return "No files selected";
    return `${fileCount} file${fileCount !== 1 ? 's' : ''} selected for upload`;
  };

  return (
    <Grid item xs={12} sm={12} md={10}>
      <Grid item xs={12} sm={12} md={10}>
        <h1>Review & Submit Request</h1>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <h6>Step 4 of 5 - Final Review</h6>
        <p>Please review your request details before submitting:</p>
      </Grid>

      <Grid item xs={12} style={{ 
        border: "1px solid #e6e8ed", 
        borderRadius: "8px", 
        padding: "20px", 
        marginBottom: "20px",
        borderLeft: "4px solid #007bff"
      }}>
        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
          Project Information
        </h3>
        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Project Name:</span>
          <span>{projectInfo?.projectName}</span>
        </div>
        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Current Status:</span>
          <span>{projectInfo?.fnmaReviewStatus}</span>
        </div>
      </Grid>

      <Grid item xs={12} style={{ 
        border: "1px solid #e6e8ed", 
        borderRadius: "8px", 
        padding: "20px", 
        marginBottom: "20px",
        borderLeft: "4px solid #007bff"
      }}>
        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
          Request Details
        </h3>
        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Request Type:</span>
          <span>{requestType}</span>
        </div>

        {/* Existing Reason Codes - shown for Modify Unavailable only */}
        {requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS && existingReasonCodes?.length > 0 && (
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Existing Reason Codes:</span>
            <span>{existingReasonCodes.map(rc => rc.reasonCode).join(", ")}</span>
          </div>
        )}

        {(requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS ||
          requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS
        ) && (
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>
              {requestType === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS ? "New Reason Codes:" : "Reason Codes:"}
            </span>
            <span>{formatReasonCodes()}</span>
          </div>
        )}

        {/* ponytail: display-only rows, no new state; guards mirror existing Existing/New rows style */}
        {addedReasonCodesList?.length > 0 && (
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Added Reason Codes:</span>
            <span>{addedReasonCodesList.join(", ")}</span>
          </div>
        )}

        {removedReasonCodes?.length > 0 && (
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Removed Reason Codes:</span>
            <span>{removedReasonCodes.join(", ")}</span>
          </div>
        )}

        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Summary & Analysis:</span>
          <span style={{ whiteSpace: "pre-wrap" }}>{comments}</span>
        </div>
      </Grid>

      {(requiredDocuments.length > 0 || selectedFiles?.length > 0) && (
        <Grid item xs={12} style={{ 
          border: "1px solid #e6e8ed", 
          borderRadius: "8px", 
          padding: "20px", 
          marginBottom: "20px",
          borderLeft: "4px solid #007bff"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
            Attachments
          </h3>
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>Selected Files:</span>
            <span>{formatUploadedFiles()}</span>
          </div>
          
          {selectedFiles && selectedFiles.map((fileObj, index) => (
            <div key={index} style={{ marginBottom: "10px", display: "flex" }}>
              <span style={{ fontWeight: "600", marginRight: "10px", minWidth: "120px" }}>{fileObj.docType.name}:</span>
              <span style={{ 
                color: "#007bff",
                fontWeight: "bold"
              }}>
                📎 {fileObj.file.name}
              </span>
            </div>
          ))}
        </Grid>
      )}

      {submitError && (
        <Grid item xs={12} style={{
          background: "#f8d7da",
          color: "#721c24",
          padding: "12px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #f5c6cb"
        }}>
          <strong>Error:</strong> {submitError}
        </Grid>
      )}

      {isSubmitting && selectedFiles?.length > 0 && (
        <Grid item xs={12} style={{
          background: "#d1ecf1",
          color: "#0c5460",
          padding: "16px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #bee5eb",
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          {/* <CircularProgress
            size={24}
            style={{
              color: "#0c5460"
            }}
          /> */}
          <div>
            File upload in progress. This may take a few minutes. Please do not close this window.
          </div>
        </Grid>
      )}

      {/* <Grid item xs={12} style={{ 
        border: "1px solid #e6e8ed", 
        borderRadius: "8px", 
        padding: "20px", 
        marginBottom: "20px",
        borderLeft: "4px solid #ffc107",
        backgroundColor: "#fff3cd"
      }}>
        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
          Important Notes
        </h3>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#666" }}>
          <li>Once submitted, this request will be sent to Fannie Mae for review.</li>
          <li>Processing time may vary based on the complexity of your request.</li>
          {requestType === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS
            && (
            <li>The reviewer will determine the final status during the review process.</li>
          )}
        </ul>
      </Grid> */}

      <Grid container spacing={2} className="dialoguClass">
        <Grid item xs={12} sm={12} md={10}>
          <Grid container justifyContent="space-between">
            <Button
              type="submit"
              className="back-button"
              style={{width: "150px"}}
              onClick={handleBack}
              disabled={isSubmitting}
              form="addProjectForm"
            >
              Back
            </Button>
            <Button
              type="submit"
              onClick={handleFormSubmit}
              disabled={isSubmitting}
              className={"btn-submit" + (isSubmitting ? " btn-loading" : "")}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress
                    size={20}
                    style={{
                      color: "#fff",
                      marginRight: "10px"
                    }}
                  />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>            
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReviewAndSubmitPage;